import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { BASE_API_URL } from "./config";

/* ─── Design tokens (matches VKTemplate1 purple theme) ─────────────── */
const C = {
  bg:      "#0D0D1A",
  card:    "#12122A",
  cardEn:  "#1A1A35",
  cardTa:  "#1A2A1A",
  border:  "#2A2A4A",
  accent:  "#7C3AED",
  amber:   "#D97706",
  green:   "#25D366",
  muted:   "#8B98C8",
  white:   "#E8E8FF",
  correct: "#22C55E",
  wrong:   "#EF4444",
};

/* ─── Decode URL-safe base64 back to comma-separated IDs ──────────── */
function decodeIds(encoded) {
  if (!encoded) return [];
  try {
    const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(b64);
    return raw.split(",").map(s => s.trim()).filter(s => /^\d+$/.test(s));
  } catch {
    return [];
  }
}

/* ─── TTS helper ────────────────────────────────────────────────────── */
function speakText(text, lang, onStart, onEnd) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text.trim());
  utter.lang = lang === "ta" ? "ta-IN" : "en-US";
  utter.rate = 0.88;
  const voices = window.speechSynthesis.getVoices();
  const match = voices.find(v => lang === "ta" ? v.lang.startsWith("ta") : v.lang.startsWith("en-US"));
  if (match) utter.voice = match;
  utter.onstart = onStart || null;
  utter.onend   = onEnd   || null;
  window.speechSynthesis.speak(utter);
}

/* ─── Single Q&A card ───────────────────────────────────────────────── */
function TwoMarkCard({ q, idx }) {
  const [showEn, setShowEn] = useState(false);
  const [showTa, setShowTa] = useState(false);
  const [spkEn,  setSpkEn]  = useState(false);
  const [spkTa,  setSpkTa]  = useState(false);

  const handleSpkEn = useCallback(() => {
    if (spkEn) { window.speechSynthesis.cancel(); setSpkEn(false); return; }
    const txt = (q.question_eng_read || q.question_eng) + ". " + (q.answer_english_read || q.answer_english);
    speakText(txt, "en", () => setSpkEn(true), () => setSpkEn(false));
  }, [spkEn, q]);

  const handleSpkTa = useCallback(() => {
    if (spkTa) { window.speechSynthesis.cancel(); setSpkTa(false); return; }
    const txt = (q.question_tamil_read || q.question_tamil) + ". " + (q.answer_tamil_read || q.answer_tamil);
    speakText(txt, "ta", () => setSpkTa(true), () => setSpkTa(false));
  }, [spkTa, q]);

  return (
    <div style={{
      background: C.card,
      borderRadius: 16,
      border: `1px solid ${C.border}`,
      marginBottom: 18,
      overflow: "hidden",
      boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
      animation: `tmSlideUp 0.4s ${Math.min(idx,8)*0.06}s both cubic-bezier(.22,1,.36,1)`,
    }}>
      {/* Colored top bar */}
      <div style={{ height: 3, background: `linear-gradient(90deg,${C.accent},#EC4899)` }} />

      {/* Meta row */}
      <div style={{ padding: "10px 16px 0", display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
        <span style={{ fontSize:11, fontWeight:700, color:C.accent,
          background:"rgba(124,58,237,0.15)", border:`1px solid rgba(124,58,237,0.3)`,
          padding:"3px 10px", borderRadius:999 }}>Q {idx + 1}</span>
        {q.chapter && (
          <span style={{ fontSize:11, fontWeight:600, color:C.muted,
            background:"rgba(139,148,158,0.1)", border:`1px solid ${C.border}`,
            padding:"3px 10px", borderRadius:999 }}>{q.chapter}</span>
        )}
      </div>

      {/* Cards side by side */}
      <div style={{ padding:"14px 16px", display:"flex", gap:12, flexWrap:"wrap" }}>

        {/* ── English card ── */}
        <div style={{
          flex:"1 1 260px", background:C.cardEn, borderRadius:12,
          border:`1px solid rgba(124,58,237,0.25)`, padding:14,
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <span style={{ fontSize:10, fontWeight:800, color:C.accent, letterSpacing:1 }}>🇬🇧 ENGLISH</span>
            <button onClick={handleSpkEn} title="Read English" style={{
              background: spkEn ? "rgba(124,58,237,0.3)" : "rgba(124,58,237,0.1)",
              border:`1px solid rgba(124,58,237,0.4)`, borderRadius:8,
              color: C.accent, fontSize:15, cursor:"pointer", padding:"3px 8px",
              animation: spkEn ? "pulse 0.7s infinite alternate" : "none",
            }}>📢</button>
          </div>

          {/* Question */}
          <div style={{ fontWeight:700, fontSize:14, color:C.white, lineHeight:1.6, marginBottom:8 }}>
            {q.question_eng}
          </div>

          {/* Reveal answer */}
          {showEn ? (
            <div style={{
              background:"rgba(124,58,237,0.08)", borderRadius:10, padding:"10px 12px",
              border:`1px solid rgba(124,58,237,0.2)`,
            }}>
              <div style={{ fontSize:10, fontWeight:800, color:C.correct, marginBottom:6, letterSpacing:1 }}>✅ ANSWER</div>
              <div style={{ fontSize:13, color:"#C4B5FD", lineHeight:1.7 }}>{q.answer_english}</div>
            </div>
          ) : (
            <button onClick={() => setShowEn(true)} style={{
              width:"100%", background:"rgba(124,58,237,0.12)",
              border:`1px dashed rgba(124,58,237,0.4)`, borderRadius:10,
              color:C.muted, fontSize:12, fontWeight:600, padding:"8px 0",
              cursor:"pointer", transition:"background 0.15s",
            }}>Tap to reveal answer ↩</button>
          )}

          {showEn && q.slide && (
            <div style={{ marginTop:8 }}>
              <img src={`/resources/${q.slide}`} alt="slide" style={{ width:"100%", borderRadius:8 }} />
            </div>
          )}
        </div>

        {/* ── Tamil card ── */}
        <div style={{
          flex:"1 1 260px", background:C.cardTa, borderRadius:12,
          border:`1px solid rgba(217,119,6,0.25)`, padding:14,
          fontFamily:"'Noto Sans Tamil', sans-serif",
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <span style={{ fontSize:10, fontWeight:800, color:C.amber, letterSpacing:1 }}>🇮🇳 தமிழ்</span>
            <button onClick={handleSpkTa} title="Read Tamil" style={{
              background: spkTa ? "rgba(217,119,6,0.3)" : "rgba(217,119,6,0.1)",
              border:`1px solid rgba(217,119,6,0.4)`, borderRadius:8,
              color:C.amber, fontSize:15, cursor:"pointer", padding:"3px 8px",
              animation: spkTa ? "pulse 0.7s infinite alternate" : "none",
            }}>🔊</button>
          </div>

          {/* Question */}
          <div style={{ fontWeight:700, fontSize:14, color:C.white, lineHeight:1.6, marginBottom:8 }}>
            {q.question_tamil}
          </div>

          {/* Reveal answer */}
          {showTa ? (
            <div style={{
              background:"rgba(217,119,6,0.08)", borderRadius:10, padding:"10px 12px",
              border:`1px solid rgba(217,119,6,0.2)`,
            }}>
              <div style={{ fontSize:10, fontWeight:800, color:C.correct, marginBottom:6, letterSpacing:1 }}>✅ பதில்</div>
              <div style={{ fontSize:13, color:"#FDE68A", lineHeight:1.7 }}>{q.answer_tamil}</div>
            </div>
          ) : (
            <button onClick={() => setShowTa(true)} style={{
              width:"100%", background:"rgba(217,119,6,0.12)",
              border:`1px dashed rgba(217,119,6,0.4)`, borderRadius:10,
              color:C.muted, fontSize:12, fontWeight:600, padding:"8px 0",
              cursor:"pointer", transition:"background 0.15s",
            }}>தட்டி பதிலைக் காண்க ↩</button>
          )}

          {showTa && q.slide_tamil && (
            <div style={{ marginTop:8 }}>
              <img src={`/resources/${q.slide_tamil}`} alt="slide" style={{ width:"100%", borderRadius:8 }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────── */
export default function SharedTwoMarkPage() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(false);

  useEffect(() => {
    // Load voices early so TTS works immediately
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    }

    const encoded = params.get("q");
    const ids = decodeIds(encoded);

    if (ids.length === 0) { setLoading(false); setError(true); return; }

    fetch(`${BASE_API_URL}/sharedtwomark?ids=${ids.join(",")}`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data) || data.length === 0) { setError(true); }
        else { setQuestions(data); }
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, [params]);

  /* ── Loading ── */
  if (loading) return (
    <div style={{ background:C.bg, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:48, height:48, borderRadius:"50%",
          border:"4px solid rgba(124,58,237,0.2)", borderTopColor:C.accent,
          animation:"spin 0.8s linear infinite", margin:"0 auto 16px" }} />
        <div style={{ color:C.muted, fontSize:14 }}>Loading shared questions…</div>
      </div>
    </div>
  );

  /* ── Error / invalid link ── */
  if (error || questions.length === 0) return (
    <div style={{ background:C.bg, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ textAlign:"center", maxWidth:400 }}>
        <div style={{ fontSize:52, marginBottom:16 }}>🔗</div>
        <div style={{ color:C.white, fontWeight:700, fontSize:20, marginBottom:8 }}>Link not found</div>
        <div style={{ color:C.muted, fontSize:14, marginBottom:24 }}>
          This shared link may have expired or contains invalid question IDs.
        </div>
        <button onClick={() => navigate("/")} style={{
          background:`linear-gradient(90deg,${C.accent},#EC4899)`,
          color:"#fff", border:"none", borderRadius:50, padding:"10px 28px",
          fontWeight:700, fontSize:14, cursor:"pointer"
        }}>🏠 Go to VirtualKalvi</button>
      </div>
    </div>
  );

  /* ── Main render ── */
  return (
    <div style={{ background:C.bg, minHeight:"100vh", fontFamily:"'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Noto+Sans+Tamil:wght@400;600;700&display=swap');
        * { box-sizing: border-box; margin:0; padding:0; }
        @keyframes tmSlideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse { from{box-shadow:0 0 0 0 rgba(124,58,237,0.5)} to{box-shadow:0 0 0 8px transparent} }
        ::-webkit-scrollbar { width:4px }
        ::-webkit-scrollbar-thumb { background:${C.accent}; border-radius:10px }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        background:"linear-gradient(135deg,#12122A,#1A1A40)",
        borderBottom:`1px solid ${C.border}`,
        padding:"14px 20px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        position:"sticky", top:0, zIndex:100,
        boxShadow:"0 2px 16px rgba(0,0,0,0.5)",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}
          onClick={() => navigate("/")}>
          <div style={{ fontSize:22 }}>📚</div>
          <span style={{ color:C.white, fontWeight:700, fontSize:16 }}>VirtualKalvi</span>
        </div>
        <span style={{ color:C.muted, fontSize:12 }}>Shared Questions</span>
      </div>

      {/* ── Banner ── */}
      <div style={{
        background:"linear-gradient(90deg,rgba(37,99,235,0.25),rgba(124,58,237,0.25))",
        borderBottom:`1px solid rgba(37,99,235,0.3)`,
        padding:"12px 20px",
        textAlign:"center",
      }}>
        <span style={{ color:"#93C5FD", fontWeight:700, fontSize:14 }}>
          📲 {questions.length} important two-mark question{questions.length !== 1 ? "s" : ""} shared via VirtualKalvi
        </span>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth:900, margin:"0 auto", padding:"20px 16px 120px" }}>

        {/* Tip */}
        <div style={{
          background:"rgba(124,58,237,0.1)", border:`1px solid rgba(124,58,237,0.25)`,
          borderRadius:12, padding:"10px 16px", marginBottom:20,
          color:C.muted, fontSize:12, display:"flex", gap:8, alignItems:"center"
        }}>
          <span>💡</span>
          <span>Tap <strong style={{color:"#C4B5FD"}}>"Tap to reveal answer"</strong> on each card to check the answer.
          Use 📢 / 🔊 to listen in English or Tamil.</span>
        </div>

        {/* Cards */}
        {questions.map((q, i) => (
          <TwoMarkCard key={q.id || i} q={q} idx={i} />
        ))}

        {/* Footer CTA */}
        <div style={{
          textAlign:"center", marginTop:32,
          background:`linear-gradient(135deg,${C.card},#1A1A40)`,
          borderRadius:16, padding:"24px 20px",
          border:`1px solid ${C.border}`,
        }}>
          <div style={{ fontSize:32, marginBottom:8 }}>📚</div>
          <div style={{ color:C.white, fontWeight:700, fontSize:16, marginBottom:6 }}>
            Want more practice?
          </div>
          <div style={{ color:C.muted, fontSize:13, marginBottom:16 }}>
            Explore thousands of questions on VirtualKalvi
          </div>
          <button onClick={() => navigate("/")} style={{
            background:`linear-gradient(90deg,${C.accent},#EC4899)`,
            color:"#fff", border:"none", borderRadius:50,
            padding:"10px 28px", fontWeight:700, fontSize:14, cursor:"pointer",
          }}>🏠 Visit VirtualKalvi</button>
        </div>
      </div>
    </div>
  );
}
